"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { usePostHog } from "@posthog/next";
import { useHydrationSafeFeatureFlagEnabled } from "@/hooks/useHydrationSafeFeatureFlagEnabled";
import { useMetaIntegrationAvailable } from "@/hooks/useMetaIntegrationAvailable";
import {
  getIntegrationListings,
  voteForIntegration,
} from "@/actions/integrations/marketplace-actions";
import {
  disconnectNodeProvider,
  isNodeProviderConnected,
} from "@/actions/workflows/actions";
import {
  handledSessionExpiry,
  unwrapSessionExpiry,
} from "@/utils/common/sessionExpiry";
import {
  deleteIntegration,
  getConnectedIntegrations,
  getIntegrations,
} from "@/actions/integrations/actions";
import { getAgencyConnections } from "@/actions-client/settings/actions";
import FormModal from "@/components/common/FormModal";
import Icon from "@/components/library/Icon";
import { useAlert } from "@/hooks/useAlert";
import { useConnectedCalendars } from "@/hooks/calendar";
import { useOrg } from "@/hooks/useOrg";
import { CALENDAR_APPLICATION } from "@/enums/profile";
import {
  IntegrationListingStatus,
  IntegrationTag,
  MARKETPLACE_FILTER_ALL,
  MARKETPLACE_FILTER_FEATURED,
  type MarketplaceFilter,
  type IntegrationListingResponse,
  ProgressStatus,
  UnifiedIntegration,
  VoteUrgency,
} from "@/types/integrations";
import { MOMENTUM_PROVIDER } from "@/types/momentum";
import {
  isMomentumIntegrationKey,
  normalizeAlphaNum,
  toUnifiedIntegration,
} from "@/utils/integrations/marketplace-config";
import { isEuropeRegion } from "@/utils/common/region";
import DisconnectModal from "../connected-apps/DisconnectModal";
import JotFormIntegrationCard from "../jotform/JotFormIntegrationCard";
import MetaIntegrationCard from "../meta/MetaIntegrationCard";
import WhatsAppIntegrationCard from "../connected-apps/WhatsAppIntegrationCard";
import SmsIntegrationCard from "../sms/SmsIntegrationCard";
import IntegrationVoteModal from "./IntegrationVoteModal";
import IntegrationComingSoonModal, { type ComingSoonRequest } from "./IntegrationComingSoonModal";
import IntegrationAgencyConnectionModal from "./IntegrationAgencyConnectionModal";
import {
  CATEGORIES,
  buildConnectedData,
  filterIntegrations,
  sortIntegrations,
  submitVoteOrRequest,
} from "./marketplace-helpers";
import { type IntegrationWithSchema } from "./schema-utils";
import { useIntegrationConnection } from "./useIntegrationConnection";
import HeroSection from "./HeroSection";
import Sidebar from "./Sidebar";
import GridContent from "./GridContent";

const INTERNAL_GOOGLE_DRIVE_LISTING: IntegrationListingResponse = {
  id: "internal-google-drive-workflows",
  key: "google_drive",
  name: "Google Drive",
  tags: [IntegrationTag.Developer],
  descriptions: {
    en: "Connect Google Drive to use Google workflow nodes.",
  },
  logoUrl: "/icons/integrations/google-drive.svg",
  status: IntegrationListingStatus.Supported,
  route: null,
  featured: true,
  progressStatus: ProgressStatus.Live,
};

const GOOGLE_DRIVE_NORMALIZED_KEY = "googledrive";
const GOOGLE_CALENDAR_NORMALIZED_KEY = "googlecalendar";
const GAIL_CALENDAR_NORMALIZED_KEY = "gailcalendar";
const CALENDLY_NORMALIZED_KEY = "calendly";

function upsertInternalGoogleDriveListing(
  listings: IntegrationListingResponse[]
): IntegrationListingResponse[] {
  const hasGoogleDriveListing = listings.some(
    (listing) =>
      normalizeAlphaNum(listing.key) === GOOGLE_DRIVE_NORMALIZED_KEY ||
      normalizeAlphaNum(listing.name) === GOOGLE_DRIVE_NORMALIZED_KEY
  );
  return hasGoogleDriveListing
    ? listings
    : [INTERNAL_GOOGLE_DRIVE_LISTING, ...listings];
}

function isGoogleDriveIntegration(integration: UnifiedIntegration): boolean {
  return (
    normalizeAlphaNum(integration.key ?? "") === GOOGLE_DRIVE_NORMALIZED_KEY ||
    normalizeAlphaNum(integration.name) === GOOGLE_DRIVE_NORMALIZED_KEY
  );
}

function withGoogleDriveConnectionState(
  integrations: UnifiedIntegration[],
  isConnected: boolean
): UnifiedIntegration[] {
  return integrations.map((integration) =>
    isGoogleDriveIntegration(integration)
      ? { ...integration, connected: isConnected }
      : integration
  );
}

function withCalendarConnectionState(
  integrations: UnifiedIntegration[],
  gailCalendarConnected: boolean,
  googleCalendarConnected: boolean
): UnifiedIntegration[] {
  return integrations.map((integration) => {
    const normalizedKey = normalizeAlphaNum(integration.key ?? "");
    const normalizedName = normalizeAlphaNum(integration.name);
    const matches = (calendarKey: string) =>
      normalizedKey === calendarKey || normalizedName === calendarKey;

    if (matches(GAIL_CALENDAR_NORMALIZED_KEY)) {
      return { ...integration, connected: gailCalendarConnected };
    }
    if (matches(GOOGLE_CALENDAR_NORMALIZED_KEY)) {
      return { ...integration, connected: googleCalendarConnected };
    }
    return integration;
  });
}

function filterRegionListings(
  listings: IntegrationListingResponse[],
  shouldShowCalendly: boolean
): IntegrationListingResponse[] {
  if (shouldShowCalendly) return listings;

  return listings.filter((listing) => {
    const normalizedKey = normalizeAlphaNum(listing.key);
    const normalizedName = normalizeAlphaNum(listing.name);
    return normalizedKey !== CALENDLY_NORMALIZED_KEY && normalizedName !== CALENDLY_NORMALIZED_KEY;
  });
}

export default function IntegrationsMarketplace() {
  const searchParams = useSearchParams();
  const alertContext = useAlert();
  const { t: vt } = useTranslation("validation");
  const { t: ct } = useTranslation("common");
  const { t, i18n } = useTranslation("partners");
  const { org } = useOrg();
  const {
    data: connectedCalendarsData,
    isLoading: isCalendarConnectionLoading,
  } = useConnectedCalendars();

  const gailCalendarConnected =
    org.features?.calendarApplication === CALENDAR_APPLICATION.GAIL &&
    !!org.services?.calendarTeamId &&
    !!org.services?.calendarDefaultEventId;
  const googleCalendarConnected =
    connectedCalendarsData?.connectedCalendars?.some(
      (calendar) => calendar.integration.type === "google_calendar"
    ) ?? false;

  const [selectedCategory, setSelectedCategory] = useState<MarketplaceFilter>(
    MARKETPLACE_FILTER_FEATURED
  );
  const [integrations, setIntegrations] = useState<UnifiedIntegration[]>([]);
  const [highlightedIntegrationId, setHighlightedIntegrationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const appliedDeepLinkRef = useRef<string | null>(null);
  const calendarAwareIntegrations = useMemo(
    () =>
      withCalendarConnectionState(
        integrations,
        gailCalendarConnected,
        googleCalendarConnected
      ),
    [integrations, gailCalendarConnected, googleCalendarConnected]
  );
  const isMarketplaceLoading = isLoading || isCalendarConnectionLoading;

  const posthog = usePostHog();
  const whatsAppEnabled = useHydrationSafeFeatureFlagEnabled("ui-whats-app-integration");
  const smsEnabled = useHydrationSafeFeatureFlagEnabled("ui-sms-integration");
  const jotFormEnabled = useHydrationSafeFeatureFlagEnabled("ui-jotform-integration");
  const shouldShowCalendly = !isEuropeRegion();
  // Shared with the Start node's Meta section, so a flag rename cannot break one
  // entry point silently.
  const shouldShowMeta = useMetaIntegrationAvailable();

  const [voteModalOpen, setVoteModalOpen] = useState(false);

  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);
  const [comingSoonTarget, setComingSoonTarget] = useState<UnifiedIntegration | null>(null);

  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [disconnectTarget, setDisconnectTarget] = useState<UnifiedIntegration | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const connectedMapRef = useRef<Map<string, string>>(new Map());

  const [availableIntegrations, setAvailableIntegrations] = useState<IntegrationWithSchema[]>([]);

  const markConnected = useCallback((integrationId: string, raccoonId?: string) => {
    setIntegrations((prev) =>
      prev.map((int) => {
        if (int.id !== integrationId) return int;
        if (raccoonId && int.key) {
          connectedMapRef.current.set(normalizeAlphaNum(int.key), raccoonId);
        }
        return { ...int, connected: true };
      })
    );
  }, []);

  const connection = useIntegrationConnection({
    availableIntegrations,
    onConnected: markConnected,
    vt,
  });

  const hasFiredViewRef = useRef(false);
  const integrationQuery = (searchParams.get("integration") ?? "").trim();

  useEffect(() => {
    if (
      !isMarketplaceLoading &&
      calendarAwareIntegrations.length > 0 &&
      !hasFiredViewRef.current
    ) {
      hasFiredViewRef.current = true;
      posthog?.capture("integration_marketplace_viewed", {
        initial_category: selectedCategory,
        total_integrations: calendarAwareIntegrations.length,
        supported_count: calendarAwareIntegrations.filter(
          (i) => i.status === IntegrationListingStatus.Supported
        ).length,
        connected_count: calendarAwareIntegrations.filter((i) => i.connected).length,
      });
    }
  }, [isMarketplaceLoading, calendarAwareIntegrations, selectedCategory]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    const timeout = setTimeout(() => {
      posthog?.capture("integration_marketplace_searched", {
        search_query: trimmed,
      });
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      const [listingsResult, connected, agency, available, googleConnected] = await Promise.all([
        getIntegrationListings().catch(() => ({ success: false as const, error: "Failed" })),
        getConnectedIntegrations().catch(() => []),
        getAgencyConnections().catch(() => []),
        getIntegrations().catch(() => []),
        isNodeProviderConnected("google")
          .then((result) => unwrapSessionExpiry(result, false))
          .catch(() => false),
      ]);
      if (cancelled) return;

      const listings = filterRegionListings(
        upsertInternalGoogleDriveListing(listingsResult.success ? listingsResult.data : []),
        shouldShowCalendly
      );
      setAvailableIntegrations(available || []);
      const { set: connectedSet, map: connectedMap } = buildConnectedData(
        connected || [],
        agency || []
      );
      connectedMapRef.current = connectedMap;
      const unified = listings.map((listing) =>
        toUnifiedIntegration(listing, connectedSet, i18n.language)
      );
      setIntegrations(withGoogleDriveConnectionState(unified, googleConnected));
      setIsLoading(false);
    }
    load().catch(() => {
      if (!cancelled) {
        setIntegrations([]);
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [shouldShowCalendly]);

  useEffect(() => {
    if (!integrationQuery) {
      setHighlightedIntegrationId(null);
      appliedDeepLinkRef.current = null;
      return;
    }

    if (isMarketplaceLoading || calendarAwareIntegrations.length === 0) return;

    const normalizedQuery = normalizeAlphaNum(integrationQuery);
    const findByKeyOrName = (
      predicate: (normalizedKey: string, normalizedName: string) => boolean
    ) =>
      calendarAwareIntegrations.find((integration) => {
        const normalizedKey = normalizeAlphaNum(integration.key ?? "");
        const normalizedName = normalizeAlphaNum(integration.name);
        return predicate(normalizedKey, normalizedName);
      });

    const exactMatch =
      findByKeyOrName((key, name) => key === normalizedQuery || name === normalizedQuery) ??
      null;

    const googleDrivePreferredMatch =
      normalizedQuery === "google"
        ? findByKeyOrName(
            (key, name) => key.includes("googledrive") || name.includes("googledrive")
          ) ?? null
        : null;

    const partialMatch =
      normalizedQuery === "google"
        ? null
        : findByKeyOrName(
            (key, name) => key.includes(normalizedQuery) || name.includes(normalizedQuery)
          ) ?? null;

    const match = exactMatch ?? googleDrivePreferredMatch ?? partialMatch ?? null;
    if (!match) return;

    setHighlightedIntegrationId(match.id);

    if (appliedDeepLinkRef.current === normalizedQuery) return;

    setSelectedCategory(MARKETPLACE_FILTER_ALL);
    setSearchQuery(match.name);
    appliedDeepLinkRef.current = normalizedQuery;
  }, [integrationQuery, calendarAwareIntegrations, isMarketplaceLoading]);

  const displayedIntegrations = useMemo(() => {
    const filtered = filterIntegrations(
      calendarAwareIntegrations,
      selectedCategory,
      searchQuery
    );
    const sortedOrFiltered = searchQuery.trim()
      ? filtered
      : sortIntegrations(filtered, selectedCategory);

    if (!highlightedIntegrationId) return sortedOrFiltered;

    const hasHighlighted = sortedOrFiltered.some(
      (integration) => integration.id === highlightedIntegrationId
    );
    if (hasHighlighted) return sortedOrFiltered;

    const highlighted = calendarAwareIntegrations.find(
      (integration) => integration.id === highlightedIntegrationId
    );
    return highlighted ? [highlighted, ...sortedOrFiltered] : sortedOrFiltered;
  }, [
    calendarAwareIntegrations,
    selectedCategory,
    searchQuery,
    highlightedIntegrationId,
  ]);

  useEffect(() => {
    if (!highlightedIntegrationId) return;

    const card = document.querySelector<HTMLElement>(
      `[data-integration-id="${highlightedIntegrationId}"]`
    );
    if (!card || typeof card.scrollIntoView !== "function") return;

    card.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightedIntegrationId, displayedIntegrations.length]);

  const handleCategoryChange = useCallback((cat: MarketplaceFilter) => {
    setSelectedCategory(cat);
    setMobileMenuOpen(false);
    setSearchQuery("");
    posthog?.capture("integration_marketplace_category_changed", {
      category: cat,
    });
  }, []);

  const handleConnect = useCallback(
    (i: UnifiedIntegration) => {
      posthog?.capture("integration_marketplace_connect_clicked", {
        integration_id: i.id,
        integration_name: i.name,
      });
      connection.handleConnect(i);
    },
    [connection]
  );

  const handleComingSoon = useCallback((i: UnifiedIntegration) => {
    posthog?.capture("integration_marketplace_coming_soon_clicked", {
      integration_id: i.id,
      integration_name: i.name,
    });
    setComingSoonTarget(i);
    setComingSoonModalOpen(true);
  }, []);

  const handleDisconnect = useCallback((i: UnifiedIntegration) => {
    // Google Drive is an Omega node credential — it never lands in the raccoon
    // connectedMapRef, so skip that gate for it.
    if (!isGoogleDriveIntegration(i)) {
      if (!i.key || !connectedMapRef.current.has(normalizeAlphaNum(i.key))) return;
    }
    posthog?.capture("integration_marketplace_disconnect_clicked", {
      integration_id: i.id,
      integration_name: i.name,
    });
    setDisconnectTarget(i);
    setDisconnectModalOpen(true);
  }, []);

  const handleDisconnectConfirm = useCallback(async () => {
    if (!disconnectTarget?.key) {
      alertContext?.setAlert?.({
        status: "error",
        message: t("marketplaceSomethingWentWrong"),
        secondsToClose: 4,
      });
      setDisconnectModalOpen(false);
      setDisconnectTarget(null);
      return;
    }

    const isGoogleDrive = isGoogleDriveIntegration(disconnectTarget);
    const normalizedKey = normalizeAlphaNum(disconnectTarget.key);
    const raccoonId = isGoogleDrive
      ? undefined
      : connectedMapRef.current.get(normalizedKey);

    if (!isGoogleDrive && !raccoonId) {
      alertContext?.setAlert?.({
        status: "error",
        message: t("marketplaceSomethingWentWrong"),
        secondsToClose: 4,
      });
      setDisconnectModalOpen(false);
      setDisconnectTarget(null);
      return;
    }

    setIsDisconnecting(true);
    try {
      if (isGoogleDrive) {
        // An expired session means nothing was deleted, so the tile must not go
        // on to render the integration as disconnected. handledSessionExpiry
        // already raises the re-auth modal, so return early instead of falling
        // into the catch below and showing a conflicting error toast.
        if (handledSessionExpiry(await disconnectNodeProvider("google"))) {
          return;
        }
      } else if (raccoonId) {
        // Momentum is double-written (see useIntegrationConnection), so its Omega
        // row goes first. A legacy Raccoon-only connection has no Omega row, and
        // this is then a no-op.
        if (
          isMomentumIntegrationKey(disconnectTarget.key) &&
          handledSessionExpiry(await disconnectNodeProvider(MOMENTUM_PROVIDER))
        ) {
          return;
        }
        await deleteIntegration(raccoonId);
        connectedMapRef.current.delete(normalizedKey);
      }
      setIntegrations((prev) =>
        prev.map((int) => (int.id === disconnectTarget.id ? { ...int, connected: false } : int))
      );
      posthog?.capture("integration_marketplace_disconnected", {
        integration_id: disconnectTarget.id,
        integration_name: disconnectTarget.name,
      });
      alertContext?.setAlert?.({
        status: "success",
        message: ct("disconnected"),
        secondsToClose: 4,
      });
    } catch {
      posthog?.capture("integration_marketplace_disconnect_failed", {
        integration_id: disconnectTarget.id,
        integration_name: disconnectTarget.name,
      });
      alertContext?.setAlert?.({
        status: "error",
        message: t("marketplaceSomethingWentWrong"),
        secondsToClose: 4,
      });
    } finally {
      setIsDisconnecting(false);
      setDisconnectModalOpen(false);
      setDisconnectTarget(null);
    }
  }, [disconnectTarget, alertContext, t, ct]);

  const handleComingSoonSubmit = useCallback(
    async (request: ComingSoonRequest) => {
      posthog?.capture("integration_marketplace_coming_soon_submitted", {
        integration_id: request.integrationId,
        integration_name: request.integrationName,
      });
      const result = await voteForIntegration({
        integrationId: request.integrationId,
        useCase: request.useCase,
        urgency: request.urgency,
      });
      if (!result.success) {
        alertContext?.setAlert?.({
          status: "error",
          message: t("marketplaceSomethingWentWrong"),
          secondsToClose: 4,
        });
        throw new Error(result.error);
      }
      setIntegrations((prev) =>
        prev.map((int) =>
          int.id === request.integrationId ? { ...int, votes: (int.votes ?? 0) + 1 } : int
        )
      );
    },
    [alertContext, t]
  );

  const handleRequestNew = useCallback(() => {
    setVoteModalOpen(true);
  }, []);

  const handleVoteSubmit = useCallback(
    async (req: { integrationName: string; useCase: string; urgency: VoteUrgency }) => {
      try {
        await submitVoteOrRequest(req, true, setIntegrations);
        alertContext?.setAlert?.({
          status: "success",
          message: t("marketplaceRequestSubmitted"),
          secondsToClose: 10,
        });
        setVoteModalOpen(false);
      } catch {
        alertContext?.setAlert?.({
          status: "error",
          message: t("marketplaceSomethingWentWrong"),
          secondsToClose: 4,
        });
      }
    },
    [alertContext, t]
  );

  const specialCards = useMemo(
    () =>
      [
        whatsAppEnabled ? <WhatsAppIntegrationCard key="whatsapp" /> : null,
        smsEnabled ? <SmsIntegrationCard key="sms" /> : null,
        jotFormEnabled ? <JotFormIntegrationCard key="jotform" /> : null,
        shouldShowMeta ? <MetaIntegrationCard key="meta" /> : null,
      ].filter((card) => card !== null),
    [whatsAppEnabled, smsEnabled, jotFormEnabled, shouldShowMeta]
  );

  const gridTitle =
    selectedCategory === MARKETPLACE_FILTER_FEATURED
      ? t("marketplaceFeaturedApps")
      : t(CATEGORIES.find((c) => c.id === selectedCategory)?.labelKey ?? "marketplaceAll");

  return (
    <div className="space-y-8">
      <HeroSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onRequestNew={handleRequestNew}
      />

      <div id="marketplace-grid" className="flex flex-col lg:flex-row gap-6">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden flex items-center justify-between w-full px-5 py-3.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
        >
          <span>
            {selectedCategory === MARKETPLACE_FILTER_FEATURED
              ? t("marketplaceFeaturedApps")
              : t(CATEGORIES.find((c) => c.id === selectedCategory)?.labelKey ?? "marketplaceAll")}
          </span>
          <Icon
            icon="chevron-down"
            className={clsx("w-4 h-4 transition-transform", mobileMenuOpen ? "rotate-180" : "")}
          />
        </button>

        <aside className={clsx("lg:w-52 shrink-0 lg:block", mobileMenuOpen ? "block" : "hidden")}>
          <div className="sticky top-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <Sidebar
              categories={CATEGORIES}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
            />
          </div>
        </aside>

        <GridContent
          selectedCategory={selectedCategory}
          displayedIntegrations={displayedIntegrations}
          gridTitle={gridTitle}
          isLoading={isMarketplaceLoading}
          specialCards={specialCards}
          highlightedIntegrationId={highlightedIntegrationId}
          onConnect={handleConnect}
          onComingSoon={handleComingSoon}
          onDisconnect={handleDisconnect}
          onRequestNew={handleRequestNew}
        />
      </div>

      <IntegrationVoteModal
        open={voteModalOpen}
        onClose={() => setVoteModalOpen(false)}
        onSubmit={handleVoteSubmit}
      />

      <IntegrationComingSoonModal
        open={comingSoonModalOpen}
        onClose={() => {
          setComingSoonModalOpen(false);
          setComingSoonTarget(null);
        }}
        onSubmit={handleComingSoonSubmit}
        integration={
          comingSoonTarget
            ? {
                id: comingSoonTarget.id,
                name: comingSoonTarget.name,
                logoUrl: comingSoonTarget.logoUrl,
                description: comingSoonTarget.description,
              }
            : null
        }
      />

      <DisconnectModal
        show={disconnectModalOpen}
        onClose={() => {
          setDisconnectModalOpen(false);
          setDisconnectTarget(null);
        }}
        onConfirm={handleDisconnectConfirm}
        integrationName={disconnectTarget?.name ?? ""}
        isLoading={isDisconnecting}
      />

      <FormModal
        labels={{
          title: connection.connectionTarget
            ? t("marketplaceSetup", { name: connection.connectionTarget.name })
            : t("marketplaceIntegrationSetup"),
          description: connection.connectionTarget
            ? t("marketplaceConfigureNamed", { name: connection.connectionTarget.name })
            : t("marketplaceConfigureGeneric"),
        }}
        open={connection.connectionModalOpen}
        sections={connection.connectionModalSections}
        cancel={connection.handleConnectionCancel}
        save={connection.handleConnectionSave}
      />

      <IntegrationAgencyConnectionModal
        open={connection.agencyModalOpen}
        agencyTarget={connection.agencyTarget}
        username={connection.agencyUsername}
        password={connection.agencyPassword}
        isConnecting={connection.isConnectingAgency}
        onUsernameChange={connection.setAgencyUsername}
        onPasswordChange={connection.setAgencyPassword}
        onSave={connection.handleAgencyConnectionSave}
        onCancel={connection.handleAgencyConnectionCancel}
      />
    </div>
  );
}
